import {
    Button,
    cleanObject,
    FDate,
    FIDescription,
    Flexer,
    Icon,
    isDayInsideRange,
    logger,
    Modal,
    Portal,
    Spinner,
    SpinnerOverlay,
    View,
    waitForRender,
} from '@fold-dev/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TbMenu2, TbMenu4 } from 'react-icons/tb'
import { useParams } from 'react-router'
import {
    Check,
    CommonProvider,
    DateButton,
    LabelButton,
    ListButton,
    LoadingLine,
    Priority,
    PriorityButton,
    RichInput,
    RichInputOption,
    RichInputProvider,
    Tiptap,
    UserButton,
} from '../components'
import { processHTML } from '../components/rich-input/rich-input.util'
import { byName, byPosition, byText, colors, mergeUniqueArrays, searchLabels } from '../helpers'
import { useLabels, useProjectUsers, useTasks, useTeamUsers, useWorkspaceUsers } from '../hooks/api.hooks'
import { useAppStore } from '../store/app'
import { Busy } from '../App'

export const NewTaskModal = ({
    type = 'project',
    teamId,
    onDismiss,
    noUserList = false,
    defaultUsers = [],
    defaultDate = undefined,
    defaultDateEnd = undefined,
    defaultWholeday = false,
    selectProject = false,
    selectSection = false,
}) => {
    const editorRef = useRef(null)
    const { workspaceId, projectId } = useParams()
    const user = useAppStore((state) => state.user)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [complete, setComplete] = useState(false)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const workspaceUsersAPI = useWorkspaceUsers()
    const [users, setUsers] = useState(defaultUsers)
    const [start, setStart] = useState(defaultDate)
    const [end, setEnd] = useState<any>(defaultDateEnd)
    const [repeat, setRepeat] = useState(null)
    const [wholeDay, setWholeDay] = useState(defaultWholeday)
    const [labels, setLabels] = useState([])
    const [priority, setPriority] = useState<Priority>('')
    const [availableLabels, setAvailableLabels] = useState([])
    const [availableUsers, setAvailableUsers] = useState([])
    const [options, setOptions] = useState<RichInputOption[]>([])
    const tasksAPI = useTasks()
    const labelsAPI = useLabels()
    const projectUsersAPI = useProjectUsers()
    const teamUsersAPI = useTeamUsers()
    const [section, setSection] = useState(0)
    const [project, setProject] = useState(0)
    const actions = useAppStore((state) => state.actions)
    const sections = useAppStore((state) => state.sections)
    const projects = useAppStore((state) => state.projects)
    const selectableSections = useMemo(() => {
        return sections.map(({ id, title }) => ({
            value: id ?? '',
            label: title,
        }))
    }, [sections])
    const selectableProjects = useMemo(() => {
        const options = projects
            .filter(({ isPrivate }) => !isPrivate)
            .map(({ id, name }) => ({
                value: id ?? '',
                label: name,
            }))

        return [{ value: '', label: 'No project' }, ...options]
    }, [projects])

    const handleDateChange = async ({ start, end, repeat, wholeDay }) => {
        setStart(start)
        setEnd(end)
        setRepeat(repeat)
        setWholeDay(wholeDay)
    }

    const handleDateDelete = async () => {
        setStart(null)
        setEnd(null)
        setRepeat(null)
        setWholeDay(null)
    }

    const handleLabelAdd = async (label) => {
        setLabels([...labels, label])
    }

    const handleLabelDelete = async (label) => {
        setLabels(labels.filter((l) => label.id != l.id))
    }

    const handleUserAdd = async (user) => {
        setUsers([...users, { ...user }])
    }

    const handleUserDelete = async (user) => {
        setUsers(users.filter((u) => user.id != u.id))
    }

    const handleTaskCreate = async () => {
        if (title == '') return

        setLoading(true)
        setError(null)

        try {
            const entities = await processHTML(title)
            const startDate = start ? start : entities.dates ? entities.dates.start : undefined
            const endDate = end ? end : entities.dates ? entities.dates.end : undefined

            const selectedSectionId = selectableSections[section] ? selectableSections[section].value || null : null
            const selectedProjectId = selectableProjects[project] ? selectableProjects[project].value || null : null
            const finalProjectId = selectProject ? selectedProjectId : projectId

            const { id } = await tasksAPI.createv2(
                workspaceId,
                finalProjectId,
                cleanObject({
                    complete,
                    read: true,
                    description,
                    priority: priority.trim() == '' ? null : priority,
                    projectSectionId: selectedSectionId,
                    title: entities.title,
                    start: startDate ? FDate(startDate).toISOWithTimezone() : undefined,
                    end: endDate ? FDate(endDate).toISOWithTimezone() : undefined,
                    repeat: repeat ?? entities.dates ? entities.dates.repeat : undefined,
                    wholeDay: wholeDay ? wholeDay : entities.dates ? entities.dates.wholeDay : undefined,
                    users: mergeUniqueArrays(
                        users.map(({ id }) => ({ id })),
                        entities.users
                    ),
                    labels: mergeUniqueArrays(labels, entities.labels),
                })
            )

            const task = await tasksAPI.getOne(workspaceId, id)
            const url = window.location.href

            if (type == 'project' || (type == 'nav' && finalProjectId == projectId)) {
                // dont add the project color - we don't use colors in project-view
                actions.taskAdd({ sectionId: selectedSectionId, task: { ...task, projectColor: undefined } })
            } else if (type == 'today' || (type == 'nav' && url.includes('today'))) {
                if (isDayInsideRange(new Date(task.start), [sections[section].day])) {
                    actions.taskAdd({ sectionId: selectedSectionId, task, forceSort: byPosition })
                }
            } else if (type == 'calendar' || (type == 'nav' && url.includes('calendar'))) {
                actions.taskAdd({ sectionId: selectedSectionId, task })
            } else if (type == 'upcoming' || (type == 'nav' && url.includes('upcoming'))) {
                sections.map((section, index) => {
                    if (isDayInsideRange(new Date(task.start), [section.day])) {
                        actions.taskAdd({ sectionId: section.id, task, forceSort: byPosition })
                    }
                })
            } else if (
                (type == 'unassigned' && !finalProjectId) ||
                (type == 'nav' && url.includes('unassigned') && !finalProjectId)
            ) {
                actions.taskAdd({ sectionId: selectedSectionId, task, forceSort: byPosition })
            }

            setLoading(false)
            onDismiss()
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleReset = () => {
        setComplete(false)
        setTitle('')
        setDescription('')
        setUsers([])
        setStart(defaultDate)
        setEnd(defaultDateEnd)
        setRepeat(null)
        setWholeDay(defaultWholeday)
        setLabels([])
    }

    // RichInputProvider

    const handleTrigger = async (text) => {
        const word = text.trim()

        if (word.charAt(0) == '@') {
            const username = word.substring(1) || ''
            const users = teamId
                ? await teamUsersAPI.getSearch(workspaceId, teamId, username)
                : projectId
                ? await projectUsersAPI.getSearch(workspaceId, projectId, username)
                : await workspaceUsersAPI.getSearch(workspaceId, username)

            setOptions(
                users
                    .map(({ id, image, username }) => ({
                        id,
                        color: undefined,
                        image,
                        name: '@' + username,
                        type: 'user',
                        value: username,
                    }))
                    .sort(byName)
            )
        } else if (word.charAt(0) == '#') {
            const label = word.substring(1) || ''
            const labels = await labelsAPI.getSearch(workspaceId, label)
            setOptions(
                labels
                    .map(({ id, text, color }) => ({
                        id,
                        color,
                        name: '#' + text,
                        type: 'label',
                        value: text,
                    }))
                    .sort(byName)
            )
        } else {
            setOptions([])
        }
    }

    // CommonProvider

    const handleUserFilter = async (query) => {
        setError(null)

        try {
            const users = teamId
                ? await teamUsersAPI.getSearch(workspaceId, teamId, query)
                : projectId
                ? await projectUsersAPI.getSearch(workspaceId, projectId, query)
                : await workspaceUsersAPI.getSearch(workspaceId, query)

            const present = users.find((u) => u.id == user.id)
            const availableUsers = present ? users.sort(byName) : [...users, user].sort(byName)

            setAvailableUsers(availableUsers)
            setOptions(users.sort(byName).map(({ id, username }) => ({ id, name: username, type: 'user' })))
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleLabelFilter = async (query) => {
        setError(null)

        try {
            //const labels = await labelsAPI.getSearch(workspaceId, query)
            const labels = searchLabels(query)

            setAvailableLabels(labels.sort(byText))
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useEffect(() => {
        waitForRender(() => editorRef.current?.focus(), 250)
    }, [])

    return (
        <RichInputProvider
            triggers={['#', '@']}
            options={options}
            onTrigger={handleTrigger}>
            <CommonProvider
                onUserFilter={handleUserFilter}
                onLabelFilter={handleLabelFilter}
                availableLabels={availableLabels}
                availableUsers={availableUsers}
                colors={colors}>
                <Modal
                    dismissOnEscape
                    borderless
                    focusTrap
                    portal={Portal}
                    width={700}
                    height="fit-content"
                    onDismiss={onDismiss}
                    isVisible={true}
                    style={{
                        '--f-modal-body-padding': 0,
                        '--f-modal-padding': '0.5rem',
                        'marginTop': -100,
                    }}
                    footer={
                        <View
                            row
                            justifyContent="space-between"
                            width="100%"
                            gap={10}>
                            <Button
                                onClick={onDismiss}
                                m="0 auto 0 0">
                                Cancel
                            </Button>

                            <Flexer />

                            <Button onClick={handleReset}>Create & Add Another</Button>

                            <Button
                                variant="accent"
                                onClick={handleTaskCreate}>
                                Create Task
                            </Button>
                        </View>
                    }>
                    <View
                        flex={0}
                        alignItems="flex-start"
                        gap="1rem"
                        width="100%"
                        p="1.5rem"
                        column>
                        <LoadingLine
                            loading={loading}
                            variant="highlight"
                            style={{ bottom: 0, top: null }}
                        />
                        <View
                            justifyContent="flex-start"
                            width="100%"
                            gap="10px"
                            row>
                            <DateButton
                                portal={Portal}
                                buttonStyleProps={{ flexDirection: 'row-reverse' }}
                                complete={complete}
                                start={start}
                                end={end}
                                repeat={repeat}
                                wholeDay={wholeDay}
                                onDelete={handleDateDelete}
                                onDateChange={handleDateChange}
                            />

                            <PriorityButton
                                onChange={(priority) => setPriority(priority)}
                                title="Select project"
                                priority={priority}
                            />

                            {selectSection && (
                                <ListButton
                                    onChange={setSection}
                                    title="Select section"
                                    list={section}
                                    options={selectableSections}
                                />
                            )}

                            {selectProject && (
                                <ListButton
                                    onChange={setProject}
                                    title="Select project"
                                    list={project}
                                    options={selectableProjects}
                                />
                            )}

                            <Flexer />

                            {!noUserList && (
                                <UserButton
                                    users={users}
                                    onAdd={handleUserAdd}
                                    onDelete={handleUserDelete}
                                />
                            )}
                        </View>
                        <View
                            row
                            width="100%"
                            gap="10px">
                            <View>
                                <Check
                                    priority={priority}
                                    checked={complete}
                                    onCheck={() => setComplete(!complete)}
                                />
                            </View>
                            <View flex={1}>
                                <RichInput
                                    dontClearOnSubmit
                                    style={{
                                        color: complete ? 'var(--f-color-text-weakest)' : undefined,
                                        fontSize: 18,
                                    }}
                                    defaultValue={title}
                                    ref={editorRef}
                                    placeholder="Create presentation..."
                                    onSubmit={(title) => (title ? setTitle(title) : null)}
                                    onCancel={() => null}
                                    onIndent={() => null}
                                    onOutdent={() => null}
                                    onUp={() => null}
                                    onDown={() => null}
                                />
                            </View>
                        </View>

                        <View
                            row
                            gap={10}
                            alignItems="flex-start">
                            <div style={{ color: 'var(--f-color-text-weaker)' }}>
                                <Icon
                                    icon={FIDescription}
                                    size="sm"
                                />
                            </div>

                            <div>
                                <Tiptap
                                    content=""
                                    onUpdate={setDescription}
                                    workspaceId={workspaceId}
                                    projectId={projectId}
                                    teamId={teamId}
                                />
                            </div>
                        </View>

                        <View
                            wrap="wrap"
                            justifyContent="flex-start"
                            width="100%"
                            gap={3}
                            row>
                            <LabelButton
                                dontHideActionButton
                                labels={labels}
                                onAdd={handleLabelAdd}
                                onDelete={handleLabelDelete}
                            />
                        </View>
                    </View>
                </Modal>
            </CommonProvider>
        </RichInputProvider>
    )
}
